import { startOfDay } from "./analytics-utils";

export type FinancialPeriodPreset = "today" | "week" | "month" | "year" | "custom";

export interface FinancialPeriodFilter {
  preset: FinancialPeriodPreset;
  /** Jour de référence (YYYY-MM-DD) — aujourd'hui et semaine glissante. */
  date?: string;
  /** 1–12 — filtre mois. */
  month?: number;
  year?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface FinancialPeriodRange {
  start: Date;
  end: Date;
  label: string;
  trendGranularity: "day" | "month";
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return startOfDay(new Date(y, (m ?? 1) - 1, d ?? 1));
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function formatPeriodDay(d: Date): string {
  return d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatMonthYear(month: number, year: number): string {
  const label = new Date(year, month - 1, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function defaultFinancialPeriodFilter(now = new Date()): FinancialPeriodFilter {
  return {
    preset: "month",
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  };
}

export function resolveFinancialPeriodRange(
  filter: FinancialPeriodFilter,
  now = new Date(),
): FinancialPeriodRange {
  const anchor = filter.date ? parseIsoDate(filter.date) : startOfDay(now);
  const year = filter.year ?? now.getFullYear();
  const month = filter.month ?? now.getMonth() + 1;

  if (filter.preset === "today") {
    const end = addDays(anchor, 1);
    return {
      start: anchor,
      end,
      label: formatPeriodDay(anchor),
      trendGranularity: "day",
    };
  }

  if (filter.preset === "week") {
    const start = addDays(anchor, -6);
    const end = addDays(anchor, 1);
    return {
      start,
      end,
      label: `7 jours — ${formatPeriodDay(start)} → ${formatPeriodDay(anchor)}`,
      trendGranularity: "day",
    };
  }

  if (filter.preset === "month") {
    const start = startOfDay(new Date(year, month - 1, 1));
    const end = startOfDay(new Date(year, month, 1));
    const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
    return {
      start,
      end,
      label: formatMonthYear(month, year),
      trendGranularity: days <= 45 ? "day" : "month",
    };
  }

  if (filter.preset === "year") {
    const start = startOfDay(new Date(year, 0, 1));
    const end = startOfDay(new Date(year + 1, 0, 1));
    return {
      start,
      end,
      label: `Année ${year}`,
      trendGranularity: "month",
    };
  }

  const from = filter.dateFrom ? parseIsoDate(filter.dateFrom) : addDays(anchor, -29);
  const toRaw = filter.dateTo ? parseIsoDate(filter.dateTo) : anchor;
  const to = addDays(toRaw < from ? from : toRaw, 1);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000);
  return {
    start: from,
    end: to,
    label: `Du ${from.toLocaleDateString("fr-FR")} au ${addDays(to, -1).toLocaleDateString("fr-FR")}`,
    trendGranularity: days <= 45 ? "day" : "month",
  };
}

export function listFinancialYearOptions(
  years: number[],
  now = new Date(),
): number[] {
  const set = new Set(years);
  set.add(now.getFullYear());
  set.add(now.getFullYear() - 1);
  return [...set].sort((a, b) => b - a);
}
